import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BrStatus, PARTNER_STATUS_DISPLAY_TEXT, PartnerStatus } from 'types/Partner';
import partnerService from 'services/partnerService';
import { LegalPartner } from 'types/LegalPartner';
import { RevokeReason } from 'views/PartnerTabs/RejectWithdrawDialog/RejectWithdrawDialog';
import { closeAllModals, openModal } from 'reducers/modalReducer';
import PrivateIndividual from 'types/PrivateIndividual';
import { ClmPortalOutgoingPostMessageTypes } from 'utils/postMessageUtil';
import { usePostMessageToIFrame } from 'hooks/usePostMessageToIFrame';
import { isNotNullOrUndefined } from 'utils/utils';
import { AxiosError } from 'axios';

type WITHDRAW_REJECT_TYPE = PartnerStatus.WITHDRAWN | PartnerStatus.REJECTED;

const isRejectOrWithdrawType = (type: BrStatus | PartnerStatus) =>
  [PartnerStatus.WITHDRAWN, PartnerStatus.REJECTED].includes(type as WITHDRAW_REJECT_TYPE);

export const useUpdateLegalPartnerStatus = (updatedPartner: LegalPartner, type: BrStatus | PartnerStatus) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { postMessageToIFrame } = usePostMessageToIFrame();

  const revokeReasonObj =
    type === PartnerStatus.WITHDRAWN
      ? { revokeReason: RevokeReason.RM_WITHDREW_PARTNER }
      : type === PartnerStatus.REJECTED
        ? { revokeReason: RevokeReason.BANK_REJECTED_PARTNER }
        : {};

  const updatedStatusPartner = {
    ...updatedPartner,
    status: type,
    ...revokeReasonObj,
  };

  return useMutation({
    mutationFn: (data: any) =>
      partnerService.updateLegalPartner({
        ...(updatedStatusPartner as LegalPartner),
        ...data,
      }),
    onSuccess: async (newData: LegalPartner) => {
      if (isRejectOrWithdrawType(type)) {
        dispatch(
          openModal({
            modalType: 'SuccessDialog',
            modalProps: {
              successMessage: t('modals.rejectWithdraw.successMessage', {
                actionType: PARTNER_STATUS_DISPLAY_TEXT[type as keyof typeof PARTNER_STATUS_DISPLAY_TEXT],
              }),
              onClose: () => dispatch(closeAllModals()),
            },
          }),
        );
      } else {
        dispatch(closeAllModals());
      }

      if (isNotNullOrUndefined(newData.techId)) {
        queryClient.setQueryData(['fetchLegalPartner', newData.techId], newData);
        await queryClient.invalidateQueries({
          queryKey: ['getBusinessRelations', newData.techId],
        });
      }

      const wn = document.getElementById('partner-iframe') as HTMLIFrameElement;
      postMessageToIFrame({ message: ClmPortalOutgoingPostMessageTypes.RE_FETCH_PARTNER }, wn);
    },
    onError: (error: AxiosError) => {
      if (isNotNullOrUndefined(error)) {
        const isRejectionOrWithdrawal = isRejectOrWithdrawType(type);
        const actionType = isRejectionOrWithdrawal
          ? PARTNER_STATUS_DISPLAY_TEXT[type as keyof typeof PARTNER_STATUS_DISPLAY_TEXT]
          : '';

        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              technicalInfo: {
                details: isRejectionOrWithdrawal
                  ? `Error occurred during partner ${actionType} process in pcm-partner-data-mgmt`
                  : 'Error occurred during partner status update in pcm-partner-data-mgmt',
                title: isRejectionOrWithdrawal ? `Partner ${actionType} failed` : 'Partner update failed',
                userErrorMessage: isRejectionOrWithdrawal
                  ? t('modals.errorDialog.rejectionWithdrawalError', {
                      actionType: actionType,
                    })
                  : '',
              },
              httpError: error,
            },
          }),
        );
      }
    },
  });
};

export const useUpdatePrivateIndividualStatus = (updatedPartner: PrivateIndividual, type: BrStatus | PartnerStatus) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { postMessageToIFrame } = usePostMessageToIFrame();

  const isRejectType = type === PartnerStatus.REJECTED;
  const isPartnerApac = updatedPartner.intendedRole === 'APAC';

  const updatedStatusPartner = {
    ...updatedPartner,
    status: type,
    ...(type === PartnerStatus.READY_FOR_ONBOARDING && {
      brStatus: isPartnerApac ? BrStatus.DRAFT : BrStatus.READY_FOR_LINKING,
      agreementId: null,
      agreementLifecycleState: null,
      revokeReason: null,
    }),
    ...(isRejectOrWithdrawType(type) && {
      brStatus: isRejectType ? BrStatus.REJECTED : BrStatus.WITHDRAWN,
      agreementLifecycleState: 'REVOKED',
      revokeReason: isRejectType ? RevokeReason.BANK_REJECTED_PARTNER : RevokeReason.RM_WITHDREW_PARTNER,
    }),
  };

  return useMutation({
    mutationFn: (data: any) =>
      partnerService.updatePartner({
        ...(updatedStatusPartner as PrivateIndividual),
        ...data,
      }),
    onSuccess: async (newData: PrivateIndividual) => {
      if (isRejectOrWithdrawType(type)) {
        dispatch(
          openModal({
            modalType: 'SuccessDialog',
            modalProps: {
              successMessage: t('modals.rejectWithdraw.successMessage', {
                actionType: PARTNER_STATUS_DISPLAY_TEXT[type as keyof typeof PARTNER_STATUS_DISPLAY_TEXT],
              }),
              onClose: () => dispatch(closeAllModals()),
            },
          }),
        );
      } else {
        dispatch(closeAllModals());
      }

      if (isNotNullOrUndefined(newData.techId)) {
        queryClient.setQueryData(['fetchPrivateIndividual', newData.techId], newData);
        await queryClient.invalidateQueries({
          queryKey: ['fetchPrivateIndividual', newData.techId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['getBusinessRelations', newData.techId],
        });
      }

      const wn = document.getElementById('partner-iframe') as HTMLIFrameElement;
      postMessageToIFrame({ message: ClmPortalOutgoingPostMessageTypes.RE_FETCH_PARTNER }, wn);
    },
    onError: (error: AxiosError) => {
      if (isNotNullOrUndefined(error)) {
        const isRejectionOrWithdrawal = isRejectOrWithdrawType(type);
        const actionType = isRejectionOrWithdrawal
          ? PARTNER_STATUS_DISPLAY_TEXT[type as keyof typeof PARTNER_STATUS_DISPLAY_TEXT]
          : '';

        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              technicalInfo: {
                details: isRejectionOrWithdrawal
                  ? `Error occurred during partner ${actionType} process in pcm-partner-data-mgmt`
                  : 'Error occurred during partner status update in pcm-partner-data-mgmt',
                title: isRejectionOrWithdrawal ? `Partner ${actionType} failed` : 'Partner update failed',
                userErrorMessage: isRejectionOrWithdrawal
                  ? t('modals.errorDialog.rejectionWithdrawalError', {
                      actionType: actionType,
                    })
                  : '',
              },
              httpError: error,
            },
          }),
        );
      }
    },
  });
};
